import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  FormControl,
  FormError,
  FormLabel,
  Heading,
} from '@alefeducation/ui-elements-library-core';
import ConfirmationModal from './ConfirmationModal';
import CheckEmailMessage from './CheckEmailMessage';
import { containerSpace } from '../styled';
import EmailValidationInput from '../../inputs/EmailValidationInput';
import { UserType } from '../../../pages/AddMember/AddMember';
import isInCCLiframe from '../../../utils/isInCCLiframe';
import {
  CHECK_EMAIL,
  SOMETHING_WENT_WRONG_MESSAGE,
  WRONG_EMAIL_FORMAT,
} from '../../../utils/constants';

const StyledCheckEmailContainer = styled.div`
  ${containerSpace};
  border-bottom: 5px solid ${({ theme }: any) => theme.colors.primary[10]};
`;

const StyledInputAction = styled.div`
  display: flex;
  column-gap: ${({ theme }: any) => `${theme.space[16]}px`};
`;

const StyledHeading = styled(Heading)`
  margin-bottom: ${({ theme }: any) => `${theme.space[14]}px`};
`;

export interface PaginationContext {
  orgCode?: string;
  pageNumber: number;
}

interface CheckEmailProps {
  hasError?: boolean;
  email?: string;
  disabled?: boolean;
  isUserExist?: boolean;
  isEmailValid?: boolean;
  isCheckClicked?: boolean;
  isOrgMember?: boolean;
  hasUpdatePermission: boolean;

  onEmailChange?(value: string): void;

  onCheckEmailClick?(): void;

  onInputBlur?(): void;

  setIsCheckClicked?(checked: boolean): void;

  userType?: UserType;
  userId?: string;
  paginationContext?: PaginationContext;
}

const doNothing = () => {};

const CheckEmail: React.FC<CheckEmailProps> = ({
  hasError,
  isEmailValid,
  email = '',
  onEmailChange = doNothing,
  onCheckEmailClick = doNothing,
  onInputBlur = doNothing,
  setIsCheckClicked = doNothing,
  disabled = false,
  isCheckClicked = false,
  userType,
  userId,
  paginationContext,
  isOrgMember,
  hasUpdatePermission = false,
}) => {
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    onEmailChange && onEmailChange(e.target.value);
  };

  let showMessage = isCheckClicked && isEmailValid && !hasError && !disabled;
  const isUserExist = userType === UserType.EXISTING;
  const [showModal, canShowModal] = useState(true);

  return (
    <StyledCheckEmailContainer>
      <StyledHeading
        variant="heading-small"
        data-testid={'please-enter-email-label'}
      >
        1. Please enter e-mail
      </StyledHeading>

      <FormControl appearance={!isEmailValid || hasError ? 'error' : 'regular'}>
        <FormLabel data-testid={'contact-email-label'}>
          Contact e-mail
        </FormLabel>
        <StyledInputAction>
          <EmailValidationInput
            value={email}
            onBlur={onInputBlur}
            showAdornmentIcon={isUserExist && isCheckClicked && !hasError}
            onChange={(e) => {
              onChange(e);
              if (isCheckClicked) {
                setIsCheckClicked(false);
              }
            }}
          />
          <Button
            variant="secondary"
            onClick={() => {
              showMessage = false;
              canShowModal(true);
              setIsCheckClicked(true);
              onCheckEmailClick();
            }}
            data-testid={'check-email-button'}
            disabled={disabled}
          >
            {CHECK_EMAIL}
          </Button>

          {showMessage && <CheckEmailMessage userType={userType} />}
          {showMessage && userType === UserType.ORG_MEMBER && (
            <ConfirmationModal
              canShowModal={canShowModal}
              showModal={
                isInCCLiframe && showModal ? hasUpdatePermission : showModal
              }
              paginationContext={paginationContext}
              userId={userId}
              isOrgMember={isOrgMember}
            />
          )}
        </StyledInputAction>

        {!isEmailValid && (
          <FormError data-testid={'email-format-error-message'}>
            {WRONG_EMAIL_FORMAT}
          </FormError>
        )}
        {isEmailValid && hasError && (
          <FormError data-testid={'something-went-wrong-error-message'}>
            {SOMETHING_WENT_WRONG_MESSAGE}
          </FormError>
        )}
      </FormControl>
    </StyledCheckEmailContainer>
  );
};

export default CheckEmail;
